import { z } from "zod";

export const KIBBE_TYPES = ["Dramatic", "Soft Dramatic", "Flamboyant Natural", "Soft Natural", "Dramatic Classic", "Soft Classic", "Flamboyant Gamine", "Soft Gamine", "Theatrical Romantic", "Romantic"] as const;
export const LEGACY_KIBBE_TYPES = ["Natural", "Classic", "Gamine"] as const;
export const KIBBE_LOCALES = ["en", "es", "pl", "fr", "pt", "it", "id", "hi", "ko", "ru", "de"] as const;
export const kibbeSlug = (name: string) => name.toLowerCase().replace(/\s+/g, "-");
export function kibbeName(value: string | null | undefined) {
  return [...KIBBE_TYPES, ...LEGACY_KIBBE_TYPES].find(name => kibbeSlug(name) === value?.trim().toLowerCase().replace(/[ _]+/g, "-")) || null;
}

export const kibbePreferencesSchema = z.object({
  locale: z.enum(KIBBE_LOCALES).default("en"),
  heightCm: z.number().min(120).max(230),
  clothing: z.enum(["womenswear", "menswear", "mixed"]),
  occasion: z.enum(["everyday", "work", "occasions"]),
  climate: z.enum(["varied", "warm", "cold"]),
  budget: z.enum(["use-my-closet", "budget", "mid-range", "flexible"]),
  preferences: z.string().max(300).default(""),
  knownType: z.string().refine(v => !v || !!kibbeName(v)).default(""),
  comparison: z.array(z.string().refine(v => !!kibbeName(v))).max(2).default([]),
});
export type KibbePreferences = z.infer<typeof kibbePreferencesSchema>;
export const defaultKibbePreferences: KibbePreferences = { locale: "en", heightCm: 0, clothing: "womenswear", occasion: "everyday", climate: "varied", budget: "use-my-closet", preferences: "", knownType: "", comparison: [] };

// Reading a type guide is interest in a type, not a request to bypass AI typing.
// Only an explicit knownType selection opts into the styling-only path.
export function kibbeEntryPreferences(saved: KibbePreferences, entry: { knownType?: string | null; focusType?: string | null; clothing?: string | null; comparison?: string[]; analyze?: boolean }): KibbePreferences {
  const knownType = kibbeName(entry.knownType);
  const comparison = [...new Set([...(entry.comparison || []), entry.focusType].map(kibbeName).filter(Boolean))].slice(0, 2) as string[];
  const clothing = ["womenswear", "menswear", "mixed"].includes(entry.clothing || "") ? entry.clothing as KibbePreferences["clothing"] : saved.clothing;
  const analysisEntry = entry.analyze || !!entry.focusType || !!entry.clothing || !!entry.comparison?.length;
  return { ...saved, clothing, knownType: knownType || (analysisEntry ? "" : saved.knownType), comparison: comparison.length ? comparison : knownType ? [] : saved.comparison };
}

export const kibbePreviewSchema = z.object({
  suitable: z.literal(true),
  primaryType: z.enum(KIBBE_TYPES), secondaryType: z.enum(KIBBE_TYPES),
  explanation: z.string().min(15).max(1000),
  limitations: z.string().min(10).max(600),
  styleHint: z.string().min(15).max(600),
  characteristics: z.object({ boneStructure: z.string().max(200), bodyFlesh: z.string().max(200), verticalLine: z.string().max(200) }),
});
export type KibbePreview = z.infer<typeof kibbePreviewSchema>;

// Restrict Stripe return destinations to this flow, including supported locales.
export function kibbeReturnPath(value: unknown) {
  return typeof value === "string" && /^\/(?:(?:en|es|pl|fr|pt|it|id|hi|ko|ru|de)\/)?kibbe-body-type-test\/wizard$/.test(value) ? value : "/kibbe-body-type-test/wizard";
}
